/**
 * O detalhe de um trânsito **na tela SENATRAN**: o que foi enviado, o que voltou, o que falta.
 *
 * Desde 21/09 a conferência do veículo mora na Operação, e este painel só trata do que vem
 * depois dela. Os cartões seguem o mockup da Gisele (tela 1): identificação, envio
 * SENATRAN, retorno SERPRO, arrecadação, motivo/mensagem — **um assunto por cartão** (§3).
 *
 * **As ações do registro moram no rodapé deste painel** (§4, regra 2), e **ação que o
 * estado não permite não aparece** (§4, regra 4): quem diz o que é possível é o
 * `destinos_possiveis` (máquina de estados) e o `acoes_de_validacao` (devolver à operação),
 * os dois calculados pelo domínio no servidor. A exceção declarada é "Reclassificar": ele
 * aparece desabilitado e diz de que história depende (`R-07`), porque é pedido explícito do
 * mockup e esconder faria parecer que a tela esqueceu dele.
 */
import { useContext, useEffect, useState } from "react";

import { avisarQueOCenarioPodeTerMudado } from "../api/cenario";
import { avisarQueAContingenciaPodeTerMudado } from "../api/contingencia";
import { ErroDaApi, api } from "../api/cliente";
import { ContextoDaSessao } from "../api/contexto-sessao";
import {
  APRESENTACAO_DA_CLASSE,
  APRESENTACAO_DA_VALIDACAO,
  APRESENTACAO_DO_STATUS,
  ehDemonstracao,
} from "../api/tipos";
import type {
  Consultado,
  DetalheDoTransito as Detalhe,
  Enviado,
  EventoDaIntegracao,
} from "../api/tipos";
import { CartoesDoCadastro } from "./CadastroDoVeiculo";
import { corDaSituacao as corDaEntrega } from "./notificacoes";
import { ProvaDaNotificacao } from "./ProvaDaNotificacao";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Linha,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import {
  dataEHora,
  horaEmBrasilia,
  reais,
  restante,
  rotuloDoPrazo,
} from "../formato";
import {
  apresentacaoDoRetorno,
  corDaSituacao,
  desfechosPossiveis,
  descricaoDaNotificacao,
  historicoEmOrdem,
  instanteRealSeDiferente,
  rotuloDaOperacao,
  tituloDaTroca,
} from "./integracao";
import { fotoDoRegulador, miniaturaDe } from "./reguas";
import { eRegulador, supervisiona } from "../componentes/navegacao";

interface Resultado {
  texto: string;
  cor: "verde" | "vermelho" | "amarelo";
  simulado?: boolean;
}

type Acao =
  "enviar" | "consultar" | "devolver" | "corrigir" | "desfecho" | "nota";

export function DetalheDoTransito({
  detalhe,
  trilha,
  carregando,
  erro,
  aoMudar,
}: {
  detalhe: Detalhe | null;
  trilha: EventoDaIntegracao[];
  carregando: boolean;
  erro: ErroDaApi | null;
  /** `aviso` sobe para a tela: a ação costuma tirar o trânsito do filtro aceso, e aí este
   *  painel troca de registro e a frase daqui some junto. */
  aoMudar: (aviso?: string) => Promise<void>;
}) {
  const [resultado, definirResultado] = useState<Resultado | null>(null);
  const [emCurso, definirEmCurso] = useState<Acao | null>(null);
  const [historico, definirHistorico] = useState(false);
  const [motivo, definirMotivo] = useState("");
  const [corrigindo, definirCorrigindo] = useState(false);
  const [placaNova, definirPlacaNova] = useState("");
  const [categoriaNova, definirCategoriaNova] = useState("");
  const [motivoDaCorrecao, definirMotivoDaCorrecao] = useState("");
  const [desfecho, definirDesfecho] = useState("");
  const [motivoDoDesfecho, definirMotivoDoDesfecho] = useState("");
  /**
   * No pago, quando o usuário pagou (`OB-04`): a hora que a arrecadação informa, no fuso de
   * quem está na tela. Vazio é "agora". É ela que diz se foi no prazo — o boleto do último dia
   * que compensou depois do vencido vai como pago no prazo, e retira o vencido.
   */
  const [pagoEm, definirPagoEm] = useState("");
  /**
   * Trânsito com TAG que vira débito público: a cobrança da operadora (OSA) sai antes, senão o
   * usuário paga duas vezes (spec §6.5, o C04 do cronograma). O domínio recusa sem a
   * confirmação; aqui o supervisor a dá, depois de revogar na OSA.
   */
  const [revogacaoOsa, definirRevogacaoOsa] = useState(false);
  /** A nota livre (C-16): `null` fechada, texto quando aberta. Vale em qualquer estado. */
  const [nota, definirNota] = useState<string | null>(null);
  // Sem provedor (testes do painel isolado) é como não ter perfil: o desfecho não aparece.
  const quem = useContext(ContextoDaSessao)?.quem ?? null;

  /**
   * O resultado da ação morre quando a seleção muda. Consultar com cenário `PAGO` tira o
   * trânsito do recorte, a lista recarrega, e sem isto a frase "PAGO — débito liquidado"
   * ficaria pendurada embaixo do detalhe de **outro** trânsito.
   */
  const id = detalhe?.id;
  useEffect(() => {
    definirResultado(null);
    definirEmCurso(null);
    definirHistorico(false);
    definirMotivo("");
    definirCorrigindo(false);
    definirPlacaNova("");
    definirCategoriaNova("");
    definirMotivoDaCorrecao("");
    definirDesfecho("");
    definirMotivoDoDesfecho("");
    definirPagoEm("");
    definirRevogacaoOsa(false);
    definirNota(null);
  }, [id]);

  // O desfecho chega escolhido com o que as regras sugerem (R-04, CA-07): o supervisor
  // confere e informa, ou troca. Efeito à parte do de cima: quando o desfecho é informado, a
  // sugestão some com os destinos, e o resultado da ação não pode sumir junto.
  const sugerido =
    detalhe?.status_cdt_sugerido &&
    (
      desfechosPossiveis(detalhe.status, detalhe.destinos_possiveis) as string[]
    ).includes(detalhe.status_cdt_sugerido.status)
      ? detalhe.status_cdt_sugerido.status
      : "";
  useEffect(() => {
    definirDesfecho(sugerido);
  }, [id, sugerido]);

  async function agir(acao: Acao) {
    if (!detalhe) return;
    definirEmCurso(acao);
    definirResultado(null);
    let aviso: string | undefined;
    try {
      if (acao === "enviar") {
        const r = await api<Enviado>(
          `/tollvision/transitos/${detalhe.id}/enviar`,
          {
            method: "POST",
            corpo: {},
          },
        );
        if (r.na_fila_desde) {
          // A SENATRAN não respondeu, ou já estava fora do ar (`R-06`): na fila da contingência,
          // e não é erro de ninguém. O envio sai sozinho quando ela voltar.
          aviso = `${detalhe.placa ?? "Trânsito"} na fila da contingência: a SENATRAN não respondeu. O envio sai sozinho quando ela voltar, pelo prazo de 24 h.`;
          definirResultado({
            texto: `Na fila da contingência desde ${horaEmBrasilia(r.na_fila_desde)} · sai sozinho quando a SENATRAN voltar`,
            cor: "amarelo",
            simulado: r.simulado,
          });
          avisarQueAContingenciaPodeTerMudado();
        } else {
          // Contrato v3: o 201 é "recebido". O registro — ou a recusa — chega pelo retorno.
          aviso = `${detalhe.placa ?? "Trânsito"} recebido pela SENATRAN — protocolo ${r.protocolo}${r.simulado ? " (emulador)" : ""}. Aguardando o retorno.`;
          definirResultado({
            texto: `Recebido · protocolo ${r.protocolo} · tentativa ${r.tentativas} · aguardando o retorno`,
            cor: "verde",
            simulado: r.simulado,
          });
        }
      } else if (acao === "consultar") {
        const r = await api<Consultado>(
          `/tollvision/transitos/${detalhe.id}/consultar`,
          {
            method: "POST",
            corpo: {},
          },
        );
        aviso = `${detalhe.placa ?? "Trânsito"}: ${r.situacao}${r.mensagem ? ` — ${r.mensagem}` : ""}.`;
        definirResultado({
          texto: r.transicionou
            ? `${r.situacao} — ${r.mensagem ?? "sem mensagem"}`
            : `${r.situacao} — ${r.mensagem ?? "sem mensagem"} (nada mudou ainda)`,
          cor: r.transicionou ? "verde" : "amarelo",
          simulado: r.simulado,
        });
      } else if (acao === "corrigir") {
        const r = await api<Enviado>(
          `/tollvision/transitos/${detalhe.id}/retificar`,
          {
            method: "POST",
            corpo: {
              placa: placaNova.trim() || null,
              categoria: categoriaNova ? Number(categoriaNova) : null,
              motivo: motivoDaCorrecao.trim(),
            },
          },
        );
        aviso = `${detalhe.placa ?? "Trânsito"}: correção registrada.`;
        definirResultado({
          texto:
            r.status === "CAPTURADO"
              ? "Corrigido. Reenvie à SENATRAN dentro das 24 horas."
              : "Correção enviada à SENATRAN; a confirmação chega pelo retorno.",
          cor: "verde",
          simulado: r.simulado,
        });
        definirCorrigindo(false);
      } else if (acao === "nota") {
        await api(`/tollvision/transitos/${detalhe.id}/notas`, {
          method: "POST",
          corpo: { texto: (nota ?? "").trim() },
        });
        definirResultado({ texto: "Nota registrada na trilha.", cor: "verde" });
        definirNota(null);
      } else if (acao === "desfecho") {
        const r = await api<Consultado>(
          `/tollvision/transitos/${detalhe.id}/desfecho`,
          {
            method: "POST",
            corpo: {
              para: desfecho,
              motivo: motivoDoDesfecho.trim() || null,
              // `datetime-local` vem sem fuso e é lido no do navegador; vai com ele.
              pago_em:
                desfecho === "PAGO" && pagoEm
                  ? new Date(pagoEm).toISOString()
                  : null,
              revogacao_osa_confirmada: revogacaoOsa,
            },
          },
        );
        aviso = `${detalhe.placa ?? "Trânsito"}: desfecho ${r.situacao.toLowerCase()} informado à SENATRAN.`;
        definirResultado({
          texto: `${r.situacao} — ${r.mensagem ?? "informado"}`,
          cor: "verde",
          simulado: r.simulado,
        });
        definirDesfecho("");
        definirMotivoDoDesfecho("");
        definirPagoEm("");
        definirRevogacaoOsa(false);
      } else {
        await api(`/tollvision/transitos/${detalhe.id}/validacao`, {
          method: "POST",
          corpo: { acao: "DEVOLVER", motivo: motivo.trim() },
        });
        aviso = `${detalhe.placa ?? "Trânsito"} devolvido à Operação.`;
        definirResultado({
          texto:
            "Devolvido à Operação: sai desta fila até ser aprovado de novo.",
          cor: "verde",
        });
        definirMotivo("");
      }
      await aoMudar(aviso);
    } catch (falha) {
      // 409 aqui é desacordo com a SERPRO, ou a trava de validação — informação de
      // operação, não defeito. A mensagem da API já explica.
      definirResultado({
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível completar a ação.",
        cor: "vermelho",
      });
      // A tentativa que falhou também é um fato (tarefa 8): o servidor a registrou, o
      // contador de tentativas subiu e o cartão "Enviados hoje" mudou. Sem recarregar, a
      // régua, a lista e o botão ("Enviar" → "Reenviar") ficavam no estado de antes do
      // clique. Uma falha ao recarregar não apaga a mensagem do erro que importa.
      await aoMudar().catch(() => undefined);
    } finally {
      definirEmCurso(null);
      // Um cenário de uma vez pode ter sido usado aqui (tarefa 7): o seletor relê.
      avisarQueOCenarioPodeTerMudado();
    }
  }

  if (erro) {
    return (
      <Painel titulo="Detalhe do envio">
        <Estado
          tipo={erro.semLicenca ? "sem-licenca" : "erro"}
          titulo={
            erro.semLicenca
              ? "Esta organização não licenciou o módulo TollVision."
              : "Não foi possível carregar o trânsito."
          }
          detalhe={erro.message}
        />
      </Painel>
    );
  }

  if (!detalhe) {
    return (
      <Painel titulo="Detalhe do envio">
        <Estado
          tipo={carregando ? "carregando" : "vazio"}
          titulo={
            carregando
              ? "Carregando o trânsito…"
              : "Selecione um trânsito na tabela."
          }
        />
      </Painel>
    );
  }

  const status = APRESENTACAO_DO_STATUS[detalhe.status];
  const aprovado = detalhe.validacao === "APROVADO";
  const capturado = detalhe.status === "CAPTURADO";
  // Contrato v3 (ADR-034): recebido e sem retorno ainda não se reenvia — consulta-se;
  // recusado pelo retorno se corrige e reenvia, dentro das 24 horas.
  const aguardando = capturado && detalhe.retorno_senatran === "AGUARDANDO";
  const recusado = capturado && detalhe.retorno_senatran === "RECUSADA";
  const podeEnviar = capturado && aprovado && !aguardando;
  const podeConsultar = detalhe.status === "EM_PROCESSAMENTO" || aguardando;
  const podeCorrigir = recusado || detalhe.status === "EM_PROCESSAMENTO";
  const supervisor = supervisiona(quem?.perfil);
  // O poder concedente lê o detalhe (`OB-13`): a passagem, as leituras, a trilha e os
  // prazos — as ações são da concessionária, e o servidor as recusa a ele.
  const somenteLeitura = eRegulador(quem?.perfil);
  const desfechos = supervisor
    ? desfechosPossiveis(detalhe.status, detalhe.destinos_possiveis)
    : [];
  const retorno = apresentacaoDoRetorno(detalhe.retorno_senatran);
  const podeDevolver = detalhe.acoes_de_validacao.includes("DEVOLVER");
  const ultima = trilha[trilha.length - 1];
  const src = somenteLeitura
    ? fotoDoRegulador(detalhe)
    : miniaturaDe({
        referencia_externa: detalhe.referencia_externa,
        imagem_frontal_id:
          detalhe.leituras.find((l) => l.camera === "FRONTAL" && l.disponivel)
            ?.imagem_id ?? null,
        // Grupo de vistas (0022): a frente anonimizada, se houver; senão a primeira que houver.
        imagem_analise_id:
          (
            (detalhe.imagens_de_analise ?? []).find(
              (i) => i.anonimizada && i.vista === "frente",
            ) ?? (detalhe.imagens_de_analise ?? []).find((i) => i.anonimizada)
          )?.imagem_id ?? null,
      });

  return (
    <Painel titulo="Detalhe do envio">
      <header className="flex flex-wrap items-center gap-2">
        {src && (
          <img
            src={src}
            alt=""
            className="h-10 w-16 rounded-[5px] bg-mesa object-cover"
          />
        )}
        <span className="mono text-[22px] leading-none font-bold text-tinta">
          {detalhe.placa ?? "—"}
        </span>
        <Badge cor={status.cor}>{status.rotulo}</Badge>
        {ehDemonstracao(detalhe) && <Badge cor="cinza">simulado</Badge>}
        {detalhe.lock_juridico && <Badge cor="vermelho">lock jurídico</Badge>}
      </header>

      {detalhe.lock_juridico && (
        // C-16 (CA-11): o banco já recusa a edição; a tela diz por quê antes de alguém
        // tentar, e só oferece o que o lock permite — desfecho com justificativa e nota.
        <p
          role="status"
          className="rounded-cartao border border-vermelho-borda bg-vermelho-fundo p-2 text-[11.5px] text-vermelho"
        >
          <b className="block tracking-[0.06em] uppercase">
            Lock jurídico ativo
          </b>
          Débito publicado: placa, categoria, valor e proprietário estão
          congelados. Só cancelamento com justificativa altera este trânsito;
          notas continuam valendo.
        </p>
      )}

      {/* As ações moram no corpo, logo abaixo da placa, e rolam junto com o resto (25/09).
          No rodapé fixo elas eram metade da altura do painel: num notebook, o rodapé comia o
          corpo inteiro e a Gisele não via as informações do envio. */}
      {somenteLeitura ? (
        <section
          aria-label="Histórico deste trânsito"
          className="flex flex-wrap items-center gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5"
        >
          <Botao onClick={() => definirHistorico((h) => !h)}>
            <Icone.Lista />
            {historico ? "Ocultar histórico" : "Ver histórico"}
          </Botao>
          <p className="text-[10.5px] text-tinta-3">
            Leitura do poder concedente: as ações sobre o trânsito são da
            concessionária.
          </p>
        </section>
      ) : (
        <section
          aria-label="Ações para este trânsito"
          className="rounded-cartao border border-linha bg-painel-2 p-2.5"
        >
          <div className="grid w-full gap-2">
            <h4 className="text-[10.5px] font-bold tracking-[0.08em] text-tinta-3 uppercase">
              Ações para este trânsito
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {podeEnviar && (
                <Botao
                  papel="primaria"
                  disabled={emCurso !== null}
                  onClick={() => void agir("enviar")}
                >
                  <Icone.Enviar />
                  {emCurso === "enviar"
                    ? "Enviando…"
                    : detalhe.tentativas > 0 || recusado
                      ? "Reenviar à SENATRAN"
                      : "Enviar à SENATRAN"}
                </Botao>
              )}
              {podeConsultar && (
                <Botao
                  disabled={emCurso !== null}
                  onClick={() => void agir("consultar")}
                >
                  <Icone.Recarga />
                  {emCurso === "consultar"
                    ? "Consultando…"
                    : "Consultar retorno"}
                </Botao>
              )}
              {podeCorrigir && (
                <Botao
                  disabled={emCurso !== null}
                  onClick={() => definirCorrigindo((c) => !c)}
                >
                  <Icone.Lapis />
                  {corrigindo ? "Fechar correção" : "Corrigir placa/categoria"}
                </Botao>
              )}
              <Botao onClick={() => definirHistorico((h) => !h)}>
                <Icone.Lista />
                {historico ? "Ocultar histórico" : "Ver histórico"}
              </Botao>
              <Botao
                disabled={emCurso !== null}
                aria-expanded={nota !== null}
                onClick={() => definirNota((n) => (n === null ? "" : null))}
              >
                <Icone.Lapis />
                {nota === null ? "Adicionar nota" : "Fechar nota"}
              </Botao>
              {!detalhe.lock_juridico && (
                <Botao
                  disabled
                  title="A saga de reclassificação de TAG é a R-07"
                >
                  <Icone.Lapis />
                  Reclassificar (R-07)
                </Botao>
              )}
            </div>
            {nota !== null && (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <input
                  value={nota}
                  onChange={(e) => definirNota(e.target.value)}
                  aria-label="Nota"
                  maxLength={1000}
                  placeholder="O que aconteceu com este trânsito"
                  className="min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                />
                <Botao
                  disabled={emCurso !== null || nota.trim().length < 2}
                  onClick={() => void agir("nota")}
                >
                  Gravar nota
                </Botao>
              </div>
            )}
            {corrigindo && (
              <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-2">
                <input
                  value={placaNova}
                  onChange={(e) => definirPlacaNova(e.target.value)}
                  aria-label="Placa corrigida"
                  placeholder={`Placa (hoje ${detalhe.placa ?? "—"})`}
                  className="mono min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                />
                <select
                  value={categoriaNova}
                  onChange={(e) => definirCategoriaNova(e.target.value)}
                  aria-label="Categoria corrigida"
                  className="min-w-0 rounded-botao border border-linha bg-painel px-1 text-tinta"
                >
                  <option value="">cat. {detalhe.categoria ?? "—"}</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  value={motivoDaCorrecao}
                  onChange={(e) => definirMotivoDaCorrecao(e.target.value)}
                  aria-label="Motivo da correção"
                  placeholder="Motivo da correção"
                  className="min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                />
                <Botao
                  disabled={
                    emCurso !== null ||
                    motivoDaCorrecao.trim().length < 3 ||
                    (!placaNova.trim() && !categoriaNova)
                  }
                  onClick={() => void agir("corrigir")}
                >
                  Corrigir
                </Botao>
              </div>
            )}
            {desfechos.length > 0 && (
              // Desfecho e botão em cima, justificativa inteira embaixo: os três numa linha só
              // espremiam a justificativa num campo de dois dedos (25/09).
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 [&>input]:col-span-2 [&>input]:row-start-2">
                <select
                  value={desfecho}
                  onChange={(e) => definirDesfecho(e.target.value)}
                  aria-label="Desfecho a informar"
                  className="min-h-8 min-w-0 rounded-botao border border-linha bg-painel px-1 text-tinta"
                >
                  <option value="">Desfecho…</option>
                  {desfechos.map((d) => (
                    <option key={d} value={d}>
                      {APRESENTACAO_DO_STATUS[d].rotulo}
                      {d === sugerido ? " (sugerido)" : ""}
                    </option>
                  ))}
                </select>
                <input
                  value={motivoDoDesfecho}
                  onChange={(e) => definirMotivoDoDesfecho(e.target.value)}
                  aria-label="Justificativa do desfecho"
                  placeholder={
                    desfecho === "CANCELADO"
                      ? "Justificativa (obrigatória)"
                      : "Justificativa (opcional)"
                  }
                  className="min-h-8 min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                />
                {desfecho === "PENDENTE_PAGAMENTO" &&
                  detalhe.status_tag !== "NENHUMA" && (
                    <label className="col-span-2 row-start-3 flex items-center gap-2 text-[11px] text-tinta-2">
                      <input
                        type="checkbox"
                        checked={revogacaoOsa}
                        onChange={(e) => definirRevogacaoOsa(e.target.checked)}
                      />
                      Revogação da cobrança na OSA confirmada — a TAG sai antes
                      do débito público
                    </label>
                  )}
                {desfecho === "PAGO" && (
                  // Num rótulo, e não solto: o seletor `[&>input]` da grade é o da justificativa.
                  <label className="col-span-2 row-start-3 grid gap-1 text-[11px] text-tinta-3">
                    Pago em — a hora da arrecadação; vazio é agora
                    <input
                      type="datetime-local"
                      value={pagoEm}
                      onChange={(e) => definirPagoEm(e.target.value)}
                      aria-label="Pago em"
                      className="min-h-8 min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                    />
                  </label>
                )}
                <Botao
                  disabled={
                    emCurso !== null ||
                    !desfecho ||
                    (desfecho === "CANCELADO" &&
                      motivoDoDesfecho.trim().length < 5)
                  }
                  onClick={() => void agir("desfecho")}
                >
                  Informar desfecho
                </Botao>
              </div>
            )}
            {podeDevolver && (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <input
                  value={motivo}
                  onChange={(e) => definirMotivo(e.target.value)}
                  aria-label="Motivo para devolver"
                  placeholder="Motivo para devolver à Operação"
                  className="min-w-0 rounded-botao border border-linha bg-painel px-2 text-tinta"
                />
                <Botao
                  disabled={emCurso !== null || motivo.trim().length < 5}
                  onClick={() => void agir("devolver")}
                >
                  <Icone.Recarga />
                  Devolver à operação
                </Botao>
              </div>
            )}
            {resultado ? (
              <p
                className={`flex flex-wrap items-center gap-1.5 ${COR_DO_RESULTADO[resultado.cor]}`}
              >
                <span className="mono">{resultado.texto}</span>
                {resultado.simulado && <Badge cor="cinza">simulado</Badge>}
              </p>
            ) : (
              <p className="text-[10.5px] text-tinta-3">
                {!aprovado
                  ? "Não aprovado: a conferência é na tela de Operação, e o envio é recusado até lá."
                  : detalhe.destinos_possiveis.length === 0
                    ? "Estado final: este trânsito não admite mais transição."
                    : `Daqui o trânsito pode ir para ${detalhe.destinos_possiveis.join(", ").toLowerCase()}.`}
              </p>
            )}
          </div>
        </section>
      )}

      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
        <CartoesDoCadastro detalhe={detalhe} />
        <Cartao titulo="Identificação" icone={<Icone.Lista />}>
          <Linha rotulo="Placa" mono>
            {detalhe.placa ?? "—"}
          </Linha>
          <Linha rotulo="Categoria · eixos" mono>
            {detalhe.categoria ?? "—"} ·{" "}
            {detalhe.eixos ?? detalhe.eixos_detectados ?? "—"}
          </Linha>
          <Linha rotulo="Passagem" mono>
            {dataEHora(detalhe.timestamp_passagem)}
          </Linha>
          <Linha rotulo="Praça · faixa">
            {detalhe.praca} · {detalhe.faixa}
          </Linha>
          <Linha rotulo="Validação">
            <Badge cor={APRESENTACAO_DA_VALIDACAO[detalhe.validacao].cor}>
              {APRESENTACAO_DA_VALIDACAO[detalhe.validacao].rotulo}
            </Badge>
          </Linha>
          {/* A classe econômica (`OB-01`): do servidor, pela regra do domínio. A válida que
              ninguém cobrou é dita, porque o poder concedente pode contestá-la. */}
          <Linha rotulo="Classe econômica">
            <Badge cor={APRESENTACAO_DA_CLASSE[detalhe.classe_economica].cor}>
              {APRESENTACAO_DA_CLASSE[detalhe.classe_economica].rotulo}
            </Badge>
          </Linha>
          {detalhe.nunca_cobrada && (
            <p className="text-[11px] text-amarelo">
              Válida que ninguém cobrou: entra na compensação, e o poder
              concedente pode contestá-la.
            </p>
          )}
          <Linha rotulo="Aprovado por">
            {detalhe.validado_por_nome ??
              (aprovado ? "registro anterior à conferência" : "—")}
          </Linha>
        </Cartao>

        <Cartao titulo="Envio SENATRAN" icone={<Icone.Enviar />}>
          <Linha rotulo="Enviado em" mono>
            {detalhe.enviado_em ? dataEHora(detalhe.enviado_em) : "não enviado"}
          </Linha>
          {/* A fila da contingência (`R-06`): no capturado, espera a SENATRAN voltar; no não
              enviável, a passagem perdeu as 24 h nela. */}
          {detalhe.na_fila_desde && (
            <Linha rotulo="Na contingência desde" mono>
              {dataEHora(detalhe.na_fila_desde)}
              {detalhe.status === "NAO_ENVIAVEL"
                ? " · venceu as 24 h na fila"
                : " · sai sozinho quando a SENATRAN voltar"}
            </Linha>
          )}
          <Linha rotulo="Protocolo" mono>
            {detalhe.protocolo_serpro ?? "—"}
          </Linha>
          <Linha rotulo="Tentativas" mono>
            {detalhe.tentativas}
          </Linha>
          {retorno && (
            <Linha rotulo="Retorno">
              <Badge cor={retorno.cor}>{retorno.texto}</Badge>
            </Linha>
          )}
          {recusado && detalhe.motivo_senatran && (
            <p className="text-[11px] text-vermelho">
              Recusado: {detalhe.motivo_senatran}
            </p>
          )}
          {detalhe.protocolo_simulado && (
            <Linha rotulo="Integração">
              {/* "simulada", como o resto da tela diz do que não é de verdade (`N228`). */}
              <Badge cor="cinza">simulada</Badge>
            </Linha>
          )}
        </Cartao>

        <Cartao titulo="Retorno SERPRO" icone={<Icone.Recarga />}>
          <Linha rotulo="Status">
            <Badge cor={status.cor}>{status.rotulo}</Badge>
          </Linha>
          {detalhe.prazos.map((p) => (
            <Linha key={p.tipo} rotulo={rotuloDoPrazo(p.tipo)} mono>
              <span title={p.descricao}>{restante(p.vence_em)}</span>
            </Linha>
          ))}
          {detalhe.prazos.length === 0 && (
            <Linha rotulo="Prazos abertos">nenhum</Linha>
          )}
        </Cartao>

        <Cartao titulo="Arrecadação" icone={<Icone.Dinheiro />}>
          <Linha rotulo="Tarifa" mono>
            {detalhe.valor_centavos == null
              ? "sem tarifa"
              : reais(detalhe.valor_centavos)}
          </Linha>
          <Linha rotulo="TAG">{detalhe.status_tag}</Linha>
          <Linha rotulo="Origem">
            <Badge cor={ehDemonstracao(detalhe) ? "amarelo" : "cinza"}>
              {detalhe.origem}
            </Badge>
          </Linha>
        </Cartao>

        <Cartao titulo="Motivo / mensagem" icone={<Icone.Alerta />}>
          {ultima ? (
            <>
              <Linha
                rotulo={`${rotuloDaOperacao(ultima.operacao)} #${ultima.tentativa}`}
              >
                <Badge cor={corDaSituacao(ultima.situacao)}>
                  {ultima.situacao ?? "—"}
                </Badge>
              </Linha>
              <p className="text-[11px] text-tinta-2">
                {ultima.mensagem ?? "sem mensagem"}
              </p>
            </>
          ) : (
            <p className="text-[11px] text-tinta-3">
              Nenhuma troca com a SENATRAN ainda.
            </p>
          )}
        </Cartao>
      </div>

      {/* A prova do protocolo de notificação (`OB-08`), quando o regime da passagem a pede. */}
      {detalhe.protocolo && <ProvaDaNotificacao dossie={detalhe.protocolo} />}

      {historico && <Historico detalhe={detalhe} trilha={trilha} />}
    </Painel>
  );
}

/**
 * Quando o fato foi registrado no sistema, se difere do instante do fato (`O-04`, AC08). Os
 * dois são guardados, e nenhum é derivado do outro. Frase neutra de propósito: vale para o
 * relógio da demonstração à frente, para a carga histórica (fato de agosto, registrado hoje) e
 * para o evento que chega atrasado da SENATRAN.
 */
function InstanteReal({ real }: { real: string | null }) {
  if (!real) return null;
  return (
    <p className="text-[10.5px] text-tinta-3">
      Registrado no sistema em {dataEHora(real)}.
    </p>
  );
}

/**
 * `C-14` e `O-16`, num lugar só (tarefa 10): as decisões da operação, cada troca com a
 * SENATRAN — tentativa, número, resultado e mensagem — e as mudanças de status, **na ordem
 * em que aconteceram**. A recusa e o reenvio que passou ficam em sequência, e a linha de
 * falha tem a mesma proeminência da de sucesso: "não tentamos" e "tentamos e falhou" são
 * fatos diferentes. Quem relê depois de cada ação, com sucesso ou não, é a tela (tarefa 8).
 */
function Historico({
  detalhe,
  trilha,
}: {
  detalhe: Detalhe;
  trilha: EventoDaIntegracao[];
}) {
  const entradas = historicoEmOrdem(detalhe, trilha);
  return (
    <Cartao titulo="Histórico" icone={<Icone.Relogio />}>
      <ol className="grid gap-2" aria-label="Histórico do trânsito">
        {entradas.map((e, i) => (
          <li key={`${e.tipo}-${i}`} className="grid gap-0.5">
            {e.tipo === "decisao" && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[11px] text-tinta-3">
                    {dataEHora(e.em)}
                  </span>
                  <Badge cor="azul">operação</Badge>
                  <span className="text-tinta-2">
                    {e.decisao.acao.toLowerCase()} →{" "}
                    <b className="text-tinta">
                      {e.decisao.situacao_para.toLowerCase()}
                    </b>
                  </span>
                </div>
                <p className="text-[10.5px] text-tinta-3">
                  {e.decisao.usuario_nome}
                </p>
                {e.decisao.motivo && (
                  <p className="text-[10.5px] text-amarelo">
                    Motivo: {e.decisao.motivo}
                  </p>
                )}
              </>
            )}
            {e.tipo === "integracao" && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[11px] text-tinta-3">
                    {dataEHora(e.em)}
                  </span>
                  <Badge cor="cinza">SENATRAN</Badge>
                  <span className="text-tinta-2">
                    {tituloDaTroca(e.evento)}
                  </span>
                  <Badge cor={corDaSituacao(e.evento.situacao)}>
                    {e.evento.situacao ?? "—"}
                  </Badge>
                  {e.evento.simulado && <Badge cor="cinza">simulado</Badge>}
                </div>
                {e.evento.protocolo && (
                  <span className="mono text-[10.5px] text-tinta-3">
                    {e.evento.protocolo}
                  </span>
                )}
                {e.evento.mensagem && (
                  <p
                    className={`text-[10.5px] ${
                      corDaSituacao(e.evento.situacao) === "vermelho"
                        ? "text-vermelho"
                        : "text-tinta-3"
                    }`}
                  >
                    {e.evento.mensagem}
                  </p>
                )}
              </>
            )}
            {e.tipo === "status" && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[11px] text-tinta-3">
                    {dataEHora(e.em)}
                  </span>
                  <Badge cor="cinza">{e.transicao.origem}</Badge>
                  <span className="text-tinta-2">
                    {APRESENTACAO_DO_STATUS[e.transicao.status_de].rotulo} →{" "}
                    <b className="text-tinta">
                      {APRESENTACAO_DO_STATUS[e.transicao.status_para].rotulo}
                    </b>
                  </span>
                  {/* Quem decidiu, quando foi uma pessoa (o AC08 do cronograma). */}
                  {e.transicao.usuario && (
                    <span className="text-tinta-3">
                      por {e.transicao.usuario}
                    </span>
                  )}
                </div>
                <p className="text-[10.5px] text-tinta-3">
                  {e.transicao.regra}
                </p>
                {e.transicao.motivo && (
                  <p className="text-[10.5px] text-amarelo">
                    Motivo: {e.transicao.motivo}
                  </p>
                )}
                <InstanteReal
                  real={instanteRealSeDiferente(
                    e.transicao.em,
                    e.transicao.registrado_em,
                  )}
                />
              </>
            )}
            {e.tipo === "notificacao" && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[11px] text-tinta-3">
                    {dataEHora(e.em)}
                  </span>
                  <Badge cor="amarelo">aviso</Badge>
                  {/* Na demonstração nada sai de verdade — nem a CNH Digital (o AC09). */}
                  {ehDemonstracao(detalhe) && (
                    <Badge cor="cinza">simulado</Badge>
                  )}
                  <span className="text-tinta-2">
                    {descricaoDaNotificacao(e.notificacao)}
                  </span>
                  {/* Onde a entrega está (`R-08`): o rótulo é do servidor. */}
                  <Badge cor={corDaEntrega(e.notificacao.situacao)}>
                    {e.notificacao.situacao_rotulo}
                  </Badge>
                </div>
                {e.notificacao.finalidade && (
                  <p className="text-[10.5px] text-tinta-3">
                    {e.notificacao.finalidade}
                  </p>
                )}
                <InstanteReal
                  real={
                    e.notificacao.em
                      ? instanteRealSeDiferente(
                          e.notificacao.em,
                          e.notificacao.criado_em,
                        )
                      : null
                  }
                />
              </>
            )}
          </li>
        ))}
        {entradas.length === 0 && (
          <li className="text-tinta-3">Sem histórico registrado.</li>
        )}
      </ol>
    </Cartao>
  );
}

const COR_DO_RESULTADO = {
  verde: "text-verde",
  amarelo: "text-amarelo",
  vermelho: "text-vermelho",
} as const;
